import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import MontanaPriceProjectionHelper from '@app/Service/MontanaPriceProjectionHelper';
import MontanaPriceProjectionInterface from '@app/Interface/MontanaPriceProjectionInterface';
import MontanaPriceHistoryInterface from '@app/Interface/MontanaPriceHistoryInterface';
import TabulatorReferenceInterface from '@app/Interface/TabulatorReferenceInterface';
import { Title, Flex, FlexItem, Button, Card, CardHeader, CardBody, CardTitle } from '@patternfly/react-core';
import { DownloadIcon } from '@patternfly/react-icons';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import moment from 'moment-timezone';
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface MontanaPricingReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    reportType: string;
}

const MontanaPricingReport: React.FC<MontanaPricingReportProps> = ({ pageId, setLastUpdatedText, reportType }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    const [historicalTableRef, setHistoricalTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [historicalReportData, setHistoricalReportData] = React.useState<MontanaPriceHistoryInterface[]>([]);
    const [historicalLoading, setHistoricalLoading] = React.useState(true);

    const [currentTableRef, setCurrentTableRef] = React.useState<React.MutableRefObject<null>|null>(null);
    const [currentReportData, setCurrentReportData] = React.useState<MontanaPriceProjectionInterface[]>([]);
    const [currentLoading, setCurrentLoading] = React.useState(true);

    const historicalTableCallback = React.useCallback(node => {
        setHistoricalTableRef({current: node});
    }, []);

    const currentTableCallback = React.useCallback(node => {
        setCurrentTableRef({current: node});
    }, []);

    const historicalTabulatorColumns = [
        { title: 'Year', field: 'PricingYear', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'CL I', field: 'CL1-Price', formatter: 'money', formatterParams: {
            precision: 2
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim', field: 'CL1-Skim', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF', field: 'CL1-BF', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Quota', field: 'Quota', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim-QT', field: 'Skim-Quota', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF-QT', field: 'BF-Quota', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Excess', field: 'Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim-EX', field: 'Skim-Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF-EX', field: 'BF-Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];
    const currentTabulatorColumns = [
        { title: 'Month', field: 'MonthYearDisplay', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Year', field: 'PriceYear', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'CL I', field: 'Price', formatter: 'money', formatterParams: {
            precision: 2
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim', field: 'Skim', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF', field: 'BF', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Quota', field: 'Quota', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim-QT', field: 'Skim_Quota', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF-QT', field: 'BF_Quota', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Excess', field: 'Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Skim-EX', field: 'Skim_Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'BF-EX', field: 'BF_Excess', formatter: 'money', formatterParams: {
            precision: 4
        }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_PriceHistory_MT')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();
        let mounted = true;
        setHistoricalLoading(true);
        setCurrentLoading(true);
        if (userState.user?.currentFpuId > 0) {
            // Get historical data
            ReportDataHelper.getMontanaPriceHistoryData(userState.user.currentFpuId, pageId)
                .then(historicalRows => { if (mounted) setHistoricalReportData(historicalRows) })
                .catch(err => { if (mounted) setHistoricalReportData([]) })
                .finally(() => { if (mounted) setHistoricalLoading(false) });
            // Get current data
            MontanaPriceProjectionHelper.getPriceProjections(controller, userState.user.currentFpuId, pageId)
                .then(currentRows => {
                    const processedCurrentRows = currentRows
                        .sort((a, b) => a.PriceYear - b.PriceYear)
                        .map(row => {
                            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            return { ...row, MonthYearDisplay: months[row.PriceMonth - 1] }
                        });
                    if (mounted) {
                        setCurrentReportData(processedCurrentRows);
                    }

                })
                .catch(err => { if (mounted) setCurrentReportData([]) })
                .finally(() => { if (mounted) setCurrentLoading(false) });

        } else {
            if (mounted) {
                setHistoricalReportData([]);
                setCurrentReportData([]);
                setHistoricalLoading(false);
                setCurrentLoading(false);
            }

        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id]);

    const downloadReportCSV = (isHistorical: boolean = false) => {

        const ref = isHistorical ? historicalTableRef : currentTableRef;

        if (ref?.current) {
            const tabulator: TabulatorReferenceInterface = ref.current;
            const filename = `${reportType}-${isHistorical ? 'historical' : 'current'}-export.csv`;
            tabulator.table.download('csv', filename);

        } else {
            console.log('Unable to download report CSV');
        }
    };

    const getTable = (isHistorical: boolean = false) => {
        const data = isHistorical ? historicalReportData : currentReportData;
        const columns = isHistorical ? historicalTabulatorColumns : currentTabulatorColumns;
        const ref = isHistorical ? historicalTableCallback : currentTableCallback;
        const montanaFormatOptions: any = {};
        if (!isHistorical) {
            montanaFormatOptions.rowFormatter = TabulatorFormatters.montanaRowFormatter;
        }
        const pdfLink = ReportDataHelper.getReportPdfUrl(
            isHistorical ? 'milk-pricing-montana-historic' : 'milk-pricing-montana-current',
            userState.user.currentFpuId
        );
        return (
            <>
                {
                    data.length === 0
                        ? <p><em>No report data to show for the selected producer</em></p>
                        : <>
                            <Flex>
                                <FlexItem align={{ default: 'alignRight' }}>
                                    <Button className="pf-u-float-right" icon={<DownloadIcon />} onClick={e => downloadReportCSV(isHistorical)}>
                                        Download CSV
                                    </Button>
                                    <Button className="pf-u-float-right" icon={<DownloadIcon />} component="a" target="_blank" href={pdfLink}>
                                        Download PDF
                                    </Button>
                                </FlexItem>
                            </Flex>
                            <SCTabulator
                                tableRef={ref}
                                data={data}
                                columns={columns}
                                layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                                options={{
                                    ...options(`nda-report-${reportType}-${isHistorical ? 'historical' : 'current'}-sc`, true, false), ...montanaFormatOptions
                                }}  
                            />
                        </>
                }
            </>
        );
    };

    return (
        <>
            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Historic Milk Pricing</Title>
                </CardTitle>
                <CardBody>
                    {
                        historicalLoading
                            ? <ItemLoader />
                            : getTable(true)
                    }
                </CardBody>
            </Card>

            <Card className="pf-u-mb-xl">
                <CardTitle>
                    <Title headingLevel="h4">Current Milk Pricing</Title>
                </CardTitle>
                <CardBody>
                    <p className="pf-u-font-weight-bold">
                        <em>Please note, although the current month price is shown in black, it is an estimate and not finalized until the final price is announced.</em>
                    </p>
                    {
                        currentLoading
                            ? <ItemLoader />
                            : getTable()
                    }
                </CardBody>
            </Card>
        </>
    );
};

export default MontanaPricingReport;